import React, { useEffect, useState } from "react";
import { renderRichText, storyblokEditable } from "@storyblok/react";
import { FaqModel } from "./FaqModel";
import { ButtonModel } from "../../core/Models/ButtonModel";
import Button from "../../core/Button/Button";
import Hyperlink from "../../core/Hyperlink/Hyperlink";
import { HyperlinkModel } from "../../core/Models/HyperlinkModel";
import Utils from "../../core/Utils/Utils";
import RelatedFaq from "../RelatedFaq/RelatedFaq";
import { Theme } from "../../core/Models/CustomSiteConfigModel";
import { themeService } from "../../core/NotificationStripNav/ThemeService";
import YouTube from "react-youtube";
import { ReactComponent as LinkSvg } from "../../../assets/images/link-svgrepo-com.svg";
import { useNotification } from "../../core/Notification/NotificationProvider";

import "./Faq.css";

const Faq: React.FC<{ blok: FaqModel }> = ({ blok }) => {
  const { addNotification } = useNotification();
  const [theme, setTheme] = useState<Theme | null>(themeService.getTheme());
  const bodyHTML = renderRichText(blok.body) || "";

  const button: ButtonModel = {
    link: { cached_url: `category\\${blok.categories}`, linktype: "story" },
    label: blok.categories,
    borderColor: "#000000",
    color: "#FFFFFF",
    textColor: "#000000",
    hoverColor: "#FFFFFF",
  };
  const getHyperlink = (tag: string): HyperlinkModel => {
    return {
      label: tag,
      link: { cached_url: `tag\\${tag?.slice(1)}`, linktype: "story" },
    };
  };
  const href = window.location.href;
  const people = blok.people[0]?.content;
  const published_at = sessionStorage.getItem("published_at");
  const formattedDate = published_at
    ? Utils.getFormattedDate(published_at)
    : null;
  const hasImage = blok.media && blok.media?.filename ? true : false;
  const isVideo = blok.media?.filename?.endsWith(".mp4");
  const isyoutube = blok.media?.filename?.startsWith("https://www.youtube.com");
  const videoId = blok.media?.filename?.split("v=")[1]?.split("&")[0];
  const classHasImage = hasImage ? "has-image" : "";
  const handleIconClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    navigator.clipboard.writeText(window.location.href);
    addNotification("Link copied to clipboard!", "notification");
  };

  const opts = {
    playerVars: {
      autoplay: 1,
      loop: 1,
      mute: 1,
    },
  };

  useEffect(() => {
    const unsubscribe = themeService.subscribe(setTheme);
    return () => unsubscribe();
  }, []);

  return (
    <div
      {...storyblokEditable(blok)}
      className={`faq ${blok.display}`}
      style={{ backgroundColor: theme?.panelColour, color: theme?.textColor }}
    >
      <div className="container">
        <div className="container-content">
          <div className="title">{blok.title}</div>

          <div className="categories-tags">
            <div className="category">
              {blok.categories && <Button blok={button}></Button>}
            </div>
            <div className="tags">
              {blok.tags
                ? blok.tags.map((tag) => (
                    <Hyperlink
                      blok={getHyperlink(tag)}
                      key={`tag-${tag}`}
                    ></Hyperlink>
                  ))
                : null}
            </div>
          </div>

          <div className="faq-container-content">
            {blok.subtitle && (
              <div className="content-title">
                <span>{blok.subtitle}</span>
              </div>
            )}
            <div
              className={`faq-container ${classHasImage} ${blok.displayImage}`}
            >
              <div className="faq-content">
                <div className="faq-content-immage">
                  {isVideo && (
                    <video
                      autoPlay
                      loop
                      muted
                      playsInline
                      src={Utils.getCacheUrl(blok.media?.filename)}
                      data-object-fit="cover"
                      onError={(e) =>
                        Utils.handleVideoError(e, blok.media.filename)
                      }
                    ></video>
                  )}
                  {isyoutube && videoId && (
                    <YouTube videoId={videoId} opts={opts} />
                  )}
                  {!isVideo && !isyoutube && blok.media?.filename && (
                    <img
                      src={Utils.getCacheUrl(blok.media.filename)}
                      alt=""
                      onError={(e) =>
                        Utils.handleImageError(e, blok.media.filename)
                      }
                    />
                  )}
                </div>
                <div
                  className="faq-content-text"
                  dangerouslySetInnerHTML={{ __html: bodyHTML }}
                ></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="related-faq-container">
        <div className="related-faq">
          <div className="related-title">RELATED FAQS</div>
          <div className="items">
            {blok.relatedFaqs
              ? blok.relatedFaqs.map((ra) => (
                  <RelatedFaq blok={ra} key={ra.uuid} />
                ))
              : null}
          </div>
        </div>
      </div>

      <div className="author-container">
        <div className="author-content">
          <div className="author">
            {people?.image?.filename && (
              <img
                src={Utils.getCacheUrl(people.image.filename)}
                alt=""
                onError={(e) =>
                  Utils.handleImageError(e, people.image.filename)
                }
              />
            )}
            <div className="author-body">
              <div className="author-name">
                By <span>{people.firstName + " " + people.lastName}</span>
              </div>
              <div className="author-published-date">{formattedDate}</div>
            </div>
          </div>
        </div>
      </div>

      <div className="author-container">
        <div className="author-share-content">
          <div className="share-faq">SHARE THIS FAQ</div>
          <div className="share-faq-icons">
            <a
              className="icon-button-container"
              onClick={(e) => {
                handleIconClick(e);
              }}
              href={href}
            >
              <LinkSvg></LinkSvg>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Faq;
